import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  FolderSearch,
  Inbox,
  Layers,
  Plus,
  Send,
  Trash2,
  User,
} from 'lucide-react';
import {
  useSearchFolders,
  useSearchFolder,
  type SearchRule,
  type SearchRuleField,
  type SearchRuleOperator,
  type SearchFolderRulesMatch,
  type SearchFolderKind,
} from './useData';
import {
  ROLLING_RECEIVED_DATE_PRESETS,
  isReceivedDatePreset,
  type ReceivedDatePreset,
  type SearchFolderPeriodPreset,
} from '../emails/utils/receivedDatePresets';
import { useAccessibleAccounts } from '../emails/useData';
import { moduleConfig } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, Label, TextInput, Select, Switch } from '../../components/ui';
import { SearchFolderSharesSection } from './components/SearchFolderSharesSection';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { getDisplaySummaryStructured } from './getSummarySentence';

const RULE_FIELD_OPTIONS: { value: SearchRuleField; label: string }[] = [
  { value: 'from', label: 'From' },
  { value: 'to', label: 'To' },
  { value: 'cc', label: 'Cc' },
  { value: 'toOrCc', label: 'To or Cc' },
  { value: 'fromToCcBcc', label: 'From, To, Cc or Bcc' },
  { value: 'subject', label: 'Subject' },
  { value: 'body', label: 'Body' },
];

const RULE_OPERATOR_OPTIONS: { value: SearchRuleOperator; label: string }[] = [
  { value: 'contains', label: 'contains' },
  { value: 'notContains', label: "doesn't contain" },
  { value: 'is', label: 'is' },
  { value: 'isNot', label: "isn't" },
  { value: 'beginsWith', label: 'begins with' },
  { value: 'endsWith', label: 'ends with' },
];

const FOLDER_KIND_LABELS: Record<SearchFolderKind, string> = {
  search: 'Rule search',
  inbox: 'Inbox',
  combined: 'Combined inbox',
  sent: 'Sent',
  combinedSent: 'Combined sent',
  mySent: 'My sent',
  combinedMySent: 'Combined my sent',
};

type FolderMode = 'search' | 'mailbox';
type MailboxFolder = 'inbox' | 'sent' | 'mySent';
type WizardStepId = 'basics' | 'setup' | 'filters' | 'period' | 'sharing';

const WIZARD_STEP_LABELS: Record<WizardStepId, string> = {
  basics: 'Name',
  setup: 'Type',
  filters: 'Filters',
  period: 'Period',
  sharing: 'Sharing',
};

function buildWizardSteps(folderKind: SearchFolderKind, isEdit: boolean): WizardStepId[] {
  const steps: WizardStepId[] = ['basics', 'setup'];
  if (folderKind === 'search') steps.push('filters');
  steps.push('period');
  if (isEdit) steps.push('sharing');
  return steps;
}

function folderKindToMode(kind: SearchFolderKind): FolderMode {
  return kind === 'search' ? 'search' : 'mailbox';
}

function folderKindToMailbox(kind: SearchFolderKind): MailboxFolder {
  if (isMySentMailboxKind(kind)) return 'mySent';
  if (isAllSentMailboxKind(kind)) return 'sent';
  return 'inbox';
}

function folderKindIsCombined(kind: SearchFolderKind): boolean {
  return kind === 'combined' || kind === 'combinedSent' || kind === 'combinedMySent';
}

function buildFolderKind(mode: FolderMode, mailbox: MailboxFolder, combined: boolean): SearchFolderKind {
  if (mode === 'search') return 'search';
  if (mailbox === 'inbox') return combined ? 'combined' : 'inbox';
  if (mailbox === 'mySent') return combined ? 'combinedMySent' : 'mySent';
  return combined ? 'combinedSent' : 'sent';
}

function folderKindAccent(kind: SearchFolderKind): {
  ring: string;
  iconBg: string;
  badge: string;
} {
  if (isMySentMailboxKind(kind)) {
    return {
      ring: 'ring-teal-500/40 border-teal-500',
      iconBg: 'bg-teal-600 text-white dark:bg-teal-500',
      badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
    };
  }
  if (isAllSentMailboxKind(kind)) {
    return {
      ring: 'ring-emerald-500/40 border-emerald-500',
      iconBg: 'bg-emerald-600 text-white dark:bg-emerald-500',
      badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    };
  }
  if (folderKindIsCombined(kind)) {
    return {
      ring: 'ring-violet-500/40 border-violet-500',
      iconBg: 'bg-violet-600 text-white dark:bg-violet-500',
      badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
    };
  }
  if (kind === 'inbox') {
    return {
      ring: 'ring-sky-500/40 border-sky-500',
      iconBg: 'bg-sky-600 text-white dark:bg-sky-500',
      badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
    };
  }
  return {
    ring: 'ring-indigo-500/40 border-indigo-500',
    iconBg: 'bg-indigo-600 text-white dark:bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  };
}

function folderKindIcon(kind: SearchFolderKind): React.ReactNode {
  switch (kind) {
    case 'inbox':
      return <Inbox className="w-4 h-4" aria-hidden />;
    case 'combined':
    case 'combinedSent':
    case 'combinedMySent':
      return <Layers className="w-4 h-4" aria-hidden />;
    case 'sent':
      return <Send className="w-4 h-4" aria-hidden />;
    case 'mySent':
      return <User className="w-4 h-4" aria-hidden />;
    default:
      return <FolderSearch className="w-4 h-4" aria-hidden />;
  }
}

function isInboxMailboxKind(kind: SearchFolderKind): boolean {
  return kind === 'inbox' || kind === 'combined';
}

function isAllSentMailboxKind(kind: SearchFolderKind): boolean {
  return kind === 'sent' || kind === 'combinedSent';
}

function isMySentMailboxKind(kind: SearchFolderKind): boolean {
  return kind === 'mySent' || kind === 'combinedMySent';
}

function isSentLikeMailboxKind(kind: SearchFolderKind): boolean {
  return isAllSentMailboxKind(kind) || isMySentMailboxKind(kind);
}

function defaultFolderForKind(kind: SearchFolderKind): 'INBOX' | 'SENT' | null {
  if (isInboxMailboxKind(kind)) return 'INBOX';
  if (isSentLikeMailboxKind(kind)) return 'SENT';
  return null;
}

const FOLDER_OPTIONS = [
  { value: '', label: 'All folders' },
  { value: 'INBOX', label: 'Inbox' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
];

const MATCH_OPTIONS: { value: SearchFolderRulesMatch; label: string }[] = [
  { value: 'all', label: 'Match all' },
  { value: 'any', label: 'Match any' },
  { value: 'allMessages', label: 'All messages' },
];

const PERIOD_OPTIONS: { value: 'all' | 'preset' | 'year'; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'preset', label: 'Recent window' },
  { value: 'year', label: 'Calendar year' },
];

function generateRuleId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function newRule(): SearchRule {
  return {
    id: generateRuleId(),
    field: 'subject',
    operator: 'contains',
    value: '',
  };
}

function parseFolderAccountIds(folder: { accountId?: number | null; accountIds?: number[] | null }): number[] {
  if (folder.accountIds?.length) return folder.accountIds;
  if (folder.accountId != null) return [folder.accountId];
  return [];
}

function buildAccountScope(selectedIds: number[]): { accountId?: number | null; accountIds?: number[] } {
  if (selectedIds.length === 0) return { accountId: null };
  if (selectedIds.length === 1) return { accountId: selectedIds[0], accountIds: [selectedIds[0]] };
  return { accountId: null, accountIds: selectedIds };
}

type AccountScopeMode = 'single' | 'combined';

const ACCOUNT_SCOPE_OPTIONS: {
  value: AccountScopeMode;
  label: string;
  hint: string;
  detail: string;
  icon: typeof User;
}[] = [
  {
    value: 'single',
    label: 'One account',
    hint: 'Single mailbox',
    detail: 'Pick one account — or leave empty for all.',
    icon: User,
  },
  {
    value: 'combined',
    label: 'Combined',
    hint: 'Merged view',
    detail: 'Pick two or more accounts to merge into one list.',
    icon: Layers,
  },
];

function FieldGroup({
  title,
  description,
  children,
  colors,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className={cn('text-sm font-semibold', colors.text)}>{title}</h2>
        {description && <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p>}
      </div>
      {children}
    </section>
  );
}

function AccountSwitchList({
  accounts,
  selectedAccountIds,
  onToggle,
  colors,
  emptyMessage = 'No accounts available.',
}: {
  accounts: { id: number; emailAddress: string }[];
  selectedAccountIds: number[];
  onToggle: (accountId: number) => void;
  colors: ReturnType<typeof useTheme>['colors'];
  emptyMessage?: string;
}) {
  if (accounts.length === 0) {
    return <p className={cn('p-3 text-sm', colors.textMuted)}>{emptyMessage}</p>;
  }

  return (
    <div className={cn('rounded-lg border overflow-hidden divide-y', colors.border)}>
      {accounts.map((acc) => {
        const checked = selectedAccountIds.includes(acc.id);
        return (
          <div
            key={acc.id}
            className={cn(
              'flex items-center justify-between gap-3 px-3 py-2.5 transition-colors',
              checked ? 'bg-indigo-50/80 dark:bg-indigo-950/25' : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]',
            )}
          >
            <span className={cn('truncate text-sm flex-1 min-w-0', colors.text)}>{acc.emailAddress}</span>
            <Switch
              checked={checked}
              onChange={() => onToggle(acc.id)}
              size="sm"
              aria-label={`Include ${acc.emailAddress}`}
            />
          </div>
        );
      })}
    </div>
  );
}

function AccountScopePicker({
  value,
  onChange,
  colors,
}: {
  value: AccountScopeMode;
  onChange: (mode: AccountScopeMode) => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <div
      className={cn('grid grid-cols-2 rounded-xl border overflow-hidden', colors.border)}
      role="radiogroup"
      aria-label="Account scope"
    >
      {ACCOUNT_SCOPE_OPTIONS.map((opt, index) => {
        const selected = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'relative flex flex-col items-start gap-3 p-4 text-left transition-colors min-h-[7.5rem]',
              index === 0 && 'border-r',
              colors.border,
              selected
                ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                : cn(colors.surface, colors.text, 'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'),
            )}
          >
            <div className="flex w-full items-start justify-between gap-2">
              <span
                className={cn(
                  'inline-flex items-center justify-center w-9 h-9 rounded-lg',
                  selected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                )}
              >
                <Icon className="w-4 h-4" aria-hidden />
              </span>
              {selected && <Check className="w-4 h-4 shrink-0 opacity-90" aria-hidden />}
            </div>
            <span className="min-w-0">
              <span className={cn('block text-sm font-semibold', selected ? 'text-white' : colors.text)}>
                {opt.label}
              </span>
              <span
                className={cn(
                  'block text-[11px] font-medium uppercase tracking-wide mt-0.5',
                  selected ? 'text-white/75' : colors.textMuted,
                )}
              >
                {opt.hint}
              </span>
              <span className={cn('block text-xs mt-1.5 leading-snug', selected ? 'text-white/85' : colors.textMuted)}>
                {opt.detail}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function WizardStepper({
  steps,
  currentIndex,
  onStepClick,
  colors,
}: {
  steps: WizardStepId[];
  currentIndex: number;
  onStepClick: (index: number) => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <nav aria-label="Smart folder wizard progress" className={cn('border-b px-4 py-4 sm:px-6', colors.border)}>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 sm:gap-x-3">
        {steps.map((stepId, index) => {
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const clickable = index < currentIndex;
          return (
            <li key={stepId} className="flex items-center shrink-0">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStepClick(index)}
                className={cn(
                  'flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 text-left transition-colors',
                  isCurrent && 'bg-indigo-50 dark:bg-indigo-950/40',
                  clickable && 'cursor-pointer hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
                  !clickable && !isCurrent && 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold tabular-nums shrink-0',
                    isComplete && 'bg-indigo-600 text-white dark:bg-indigo-500',
                    isCurrent && 'bg-indigo-600 text-white ring-2 ring-indigo-300 dark:ring-indigo-700 dark:bg-indigo-500',
                    !isComplete && !isCurrent && cn('border', colors.border, colors.textMuted),
                  )}
                >
                  {isComplete ? <Check className="w-3.5 h-3.5" aria-hidden /> : index + 1}
                </span>
                <span
                  className={cn(
                    'text-xs sm:text-sm font-medium whitespace-nowrap',
                    isCurrent ? 'text-indigo-700 dark:text-indigo-300' : colors.textMuted,
                  )}
                >
                  {WIZARD_STEP_LABELS[stepId]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  colors,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  colors: ReturnType<typeof useTheme>['colors'];
  ariaLabel: string;
}) {
  return (
    <div
      className={cn('inline-flex flex-wrap gap-1 rounded-lg border p-1', colors.border, colors.surface)}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              selected
                ? 'bg-indigo-600 text-white shadow-sm dark:bg-indigo-500'
                : cn(colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5'),
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const isEdit = id != null;
  const folderId = id ? parseInt(id, 10) : null;

  const { mutate: globalMutate } = useSWRConfig();
  const { data: folder, isLoading: loadingFolder } = useSearchFolder(folderId ?? 0);
  const { data: accounts } = useAccessibleAccounts();
  const { data: allFolders, create, update } = useSearchFolders();

  useEffect(() => {
    if (folderId != null) globalMutate(`/emails/search-folders/${folderId}`);
  }, [folderId, globalMutate]);

  const nextDisplayOrder = useMemo(() => {
    if (allFolders.length === 0) return 0;
    return Math.max(...allFolders.map((f) => f.displayOrder)) + 1;
  }, [allFolders]);

  const [name, setName] = useState('');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [rulesMatch, setRulesMatch] = useState<SearchFolderRulesMatch>('all');
  const [rules, setRules] = useState<SearchRule[]>(() => [newRule()]);
  const [folderKind, setFolderKind] = useState<SearchFolderKind>('search');
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [folderName, setFolderName] = useState('');
  const [periodMode, setPeriodMode] = useState<'all' | 'preset' | 'year'>('all');
  const [periodPreset, setPeriodPreset] = useState<ReceivedDatePreset>('30d');
  const [periodYear, setPeriodYear] = useState<number>(new Date().getUTCFullYear());
  const [saving, setSaving] = useState(false);
  const [wizardStepIndex, setWizardStepIndex] = useState(0);

  const wizardSteps = useMemo(
    () => buildWizardSteps(folderKind, isEdit),
    [folderKind, isEdit],
  );
  const currentWizardStep = wizardSteps[wizardStepIndex] ?? wizardSteps[0];
  const isLastWizardStep = wizardStepIndex >= wizardSteps.length - 1;
  const canProceedWizard = currentWizardStep !== 'basics' || name.trim().length > 0;

  useEffect(() => {
    setWizardStepIndex((index) => Math.min(index, Math.max(0, wizardSteps.length - 1)));
  }, [wizardSteps.length]);

  const goWizardNext = useCallback(() => {
    requestAnimationFrame(() => {
      setWizardStepIndex((index) => Math.min(index + 1, wizardSteps.length - 1));
    });
  }, [wizardSteps.length]);

  const goWizardBack = useCallback(() => {
    setWizardStepIndex((index) => Math.max(index - 1, 0));
  }, []);

  const applyFolderKind = useCallback((kind: SearchFolderKind) => {
    setFolderKind(kind);
    const defaultFolder = defaultFolderForKind(kind);
    if (defaultFolder) setFolderName(defaultFolder);
  }, []);

  const folderMode = folderKindToMode(folderKind);
  const mailboxFolder = folderKindToMailbox(folderKind);
  const isCombined = folderKindIsCombined(folderKind);
  const accent = folderKindAccent(folderKind);

  const setMailboxCombined = useCallback(
    (combined: boolean) => {
      applyFolderKind(buildFolderKind('mailbox', folderKindToMailbox(folderKind), combined));
      if (!combined) {
        setSelectedAccountIds((prev) => (prev.length <= 1 ? prev : prev.slice(0, 1)));
      }
    },
    [applyFolderKind, folderKind],
  );

  const accountScopeMode: AccountScopeMode = isCombined ? 'combined' : 'single';

  const toggleAccount = useCallback(
    (accountId: number) => {
      if (folderMode === 'mailbox' && !isCombined) {
        setSelectedAccountIds((prev) => (prev.includes(accountId) ? [] : [accountId]));
        return;
      }
      setSelectedAccountIds((prev) =>
        prev.includes(accountId) ? prev.filter((x) => x !== accountId) : [...prev, accountId],
      );
    },
    [folderMode, isCombined],
  );

  const hydratedFolderIdRef = useRef<number | null>(null);

  useEffect(() => {
    hydratedFolderIdRef.current = null;
  }, [folderId]);

  useEffect(() => {
    if (folder) {
      if (hydratedFolderIdRef.current === folder.id) return;
      hydratedFolderIdRef.current = folder.id;
      setName(folder.name);
      setDisplayOrder(String(folder.displayOrder ?? 0));
      setFolderKind((folder.folderKind as SearchFolderKind) ?? 'search');
      setSelectedAccountIds(parseFolderAccountIds(folder));
      setFolderName(folder.folder ?? '');
      const match = folder.rulesMatch;
      if (match === 'all' || match === 'any' || match === 'allMessages') {
        setRulesMatch(match);
      }
      let rulesList = folder.rules;
      if (typeof rulesList === 'string') {
        try {
          rulesList = JSON.parse(rulesList) as SearchRule[];
        } catch {
          rulesList = null;
        }
      }
      if (rulesList && Array.isArray(rulesList) && rulesList.length > 0) {
        setRules(
          rulesList.map((r: Partial<SearchRule>) => ({
            id: (r as SearchRule).id || generateRuleId(),
            field: (r.field as SearchRuleField) || 'subject',
            operator: (r.operator as SearchRuleOperator) || 'contains',
            value: r.value ?? '',
          })),
        );
      } else {
        setRules([newRule()]);
      }
      if (folder.periodYear != null && Number.isFinite(folder.periodYear)) {
        setPeriodMode('year');
        setPeriodYear(folder.periodYear);
      } else if (folder.periodPreset && isReceivedDatePreset(folder.periodPreset)) {
        setPeriodMode('preset');
        setPeriodPreset(folder.periodPreset);
      } else {
        setPeriodMode('all');
      }
    } else if (!isEdit) {
      setName('');
      setDisplayOrder(String(nextDisplayOrder));
      setRulesMatch('all');
      setRules([newRule()]);
      setFolderKind('search');
      setSelectedAccountIds([]);
      setFolderName('');
      setPeriodMode('all');
      setPeriodPreset('30d');
      setPeriodYear(new Date().getUTCFullYear());
    }
  }, [folder, isEdit, nextDisplayOrder]);

  const addRule = useCallback(() => {
    setRules((prev) => [...prev, newRule()]);
  }, []);

  const removeRule = useCallback((ruleId: string) => {
    setRules((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== ruleId) : prev));
  }, []);

  const updateRule = useCallback((ruleId: string, patch: Partial<Omit<SearchRule, 'id'>>) => {
    setRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, ...patch } : r)));
  }, []);

  const summary = useMemo(
    () =>
      getDisplaySummaryStructured({
        name,
        folderKind,
        rules,
        rulesMatch: folderKind === 'search' ? rulesMatch : 'allMessages',
        periodPreset: periodMode === 'preset' ? periodPreset : 'all',
        periodYear: periodMode === 'year' ? periodYear : null,
      }),
    [name, rules, rulesMatch, folderKind, periodMode, periodPreset, periodYear],
  );

  const selectedAccountLabels = useMemo(() => {
    if (selectedAccountIds.length === 0) return 'All accessible accounts';
    return selectedAccountIds
      .map((id) => accounts?.find((a) => a.id === id)?.emailAddress ?? `#${id}`)
      .join(', ');
  }, [selectedAccountIds, accounts]);

  const saveFolder = useCallback(async () => {
    const nameTrim = name.trim();
    if (!nameTrim) return;
    const orderNum = displayOrder.trim() === '' ? 0 : parseInt(displayOrder, 10);
    if (!Number.isFinite(orderNum) || orderNum < 0) return;
    setSaving(true);
    try {
      const rulesPayload = rules
        .map((r) => ({ field: r.field, operator: r.operator, value: (r.value ?? '').trim() }))
        .filter((r) => r.value !== '' || r.operator === 'isNot' || r.operator === 'notContains');
      const accountScope = buildAccountScope(selectedAccountIds);
      const effectiveRulesMatch = folderKind === 'search' ? rulesMatch : 'allMessages';
      const mailboxFolderPath = defaultFolderForKind(folderKind);
      const effectiveFolder = mailboxFolderPath ?? (folderName.trim() || null);
      const body = {
        name: nameTrim,
        displayOrder: orderNum,
        folderKind,
        ...accountScope,
        folder: effectiveFolder,
        rules: folderKind === 'search' ? rulesPayload : [],
        rulesMatch: effectiveRulesMatch,
        periodPreset: periodMode === 'preset' ? periodPreset : ('all' as SearchFolderPeriodPreset),
        periodYear: periodMode === 'year' ? periodYear : null,
      };
      if (isEdit && folderId) {
        await update(folderId, body);
        globalMutate(`/emails/search-folders/${folderId}`);
        navigate(moduleConfig.module.route.list);
      } else {
        await create({
          name: body.name,
          folderKind: body.folderKind,
          accountId: body.accountId ?? undefined,
          accountIds: body.accountIds,
          folder: body.folder || undefined,
          rules: body.rules,
          rulesMatch: body.rulesMatch,
          periodPreset: body.periodPreset,
          periodYear: body.periodYear,
        });
        navigate(moduleConfig.module.route.list);
      }
    } finally {
      setSaving(false);
    }
  }, [
    name,
    displayOrder,
    rules,
    selectedAccountIds,
    folderKind,
    rulesMatch,
    folderName,
    periodMode,
    periodPreset,
    periodYear,
    isEdit,
    folderId,
    update,
    create,
    globalMutate,
    navigate,
  ]);

  const handleFormKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLastWizardStep) return;
    if (canProceedWizard) goWizardNext();
  };

  if (isEdit && loadingFolder && !folder) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="large" message="Loading smart folder..." />
      </div>
    );
  }
  if (isEdit && folderId && !folder) {
    return (
      <div className={cn('max-w-lg mx-auto p-8 text-center rounded-xl border', colors.border, colors.surface)}>
        <p className={cn('font-medium', colors.text)}>Smart folder not found</p>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('inline-flex items-center gap-1.5 mt-4 text-sm font-medium', colors.primaryFg)}
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col min-h-0', colors.background)}>
      <form onSubmit={handleFormKeySubmit} className="flex flex-col flex-1 min-h-0">
        {/* Sticky toolbar */}
        <div
          className={cn(
            'sticky top-0 z-20 border-b backdrop-blur-md',
            colors.border,
            'bg-white/90 dark:bg-gray-950/90',
          )}
        >
          <div className="w-full px-4 sm:px-6 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={moduleConfig.module.route.list}
                className={cn(
                  'inline-flex items-center justify-center w-9 h-9 rounded-lg border shrink-0 transition-colors',
                  colors.border,
                  colors.textMuted,
                  'hover:bg-black/5 dark:hover:bg-white/5',
                )}
                title="Back to smart folders"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden />
              </Link>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className={cn('text-lg font-semibold truncate', colors.text)}>
                    {isEdit ? (name.trim() || 'Edit smart folder') : 'New smart folder'}
                  </h1>
                  <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', accent.badge)}>
                    {FOLDER_KIND_LABELS[folderKind]}
                  </span>
                </div>
                <p className={cn('text-xs truncate', colors.textMuted)}>
                  Step {wizardStepIndex + 1} of {wizardSteps.length} · {WIZARD_STEP_LABELS[currentWizardStep]}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
              <Button type="button" onClick={() => navigate(moduleConfig.module.route.list)} variant="outline" size="sm">
                Cancel
              </Button>
            </div>
          </div>
        </div>

        <div className="w-full flex-1 min-h-0 px-4 py-5 sm:px-6 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
            {/* Main panel */}
            <div className={cn('rounded-xl border shadow-sm overflow-hidden flex flex-col', colors.border, colors.surface)}>
              <WizardStepper
                steps={wizardSteps}
                currentIndex={wizardStepIndex}
                onStepClick={setWizardStepIndex}
                colors={colors}
              />

              <div className="p-4 sm:p-6 flex-1 min-h-[22rem]">
                {currentWizardStep === 'basics' && (
                  <FieldGroup
                    title="Name your smart folder"
                    description="This label appears under Smart Folders in the Emails sidebar."
                    colors={colors}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_6rem] gap-4">
                      <div>
                        <Label htmlFor="sf-name" required>
                          Display name
                        </Label>
                        <TextInput
                          id="sf-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. POSEIDON, cv@ inbox"
                          className="w-full mt-1"
                          required
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label htmlFor="sf-order">Order</Label>
                        <TextInput
                          id="sf-order"
                          type="number"
                          min={0}
                          step={1}
                          value={displayOrder}
                          onChange={(e) => setDisplayOrder(e.target.value)}
                          className="w-full mt-1"
                          title="Sidebar order (lower = first)"
                        />
                        <p className={cn('mt-1 text-[11px]', colors.textMuted)}>Lower = first</p>
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'setup' && (
                  <div className="space-y-6">
                    <FieldGroup
                      title="How should this folder work?"
                      description="Rule search filters messages. Mailbox view shows a real folder (Inbox, Sent, or My sent)."
                      colors={colors}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {(
                            [
                              {
                                mode: 'search' as FolderMode,
                                label: 'Rule search',
                                hint: 'Filter by sender, subject, body…',
                                icon: <FolderSearch className="w-5 h-5" aria-hidden />,
                              },
                              {
                                mode: 'mailbox' as FolderMode,
                                label: 'Mailbox view',
                                hint: 'Show Inbox, Sent, or My sent from account(s)',
                                icon: <Inbox className="w-5 h-5" aria-hidden />,
                              },
                            ] as const
                          ).map((opt) => {
                            const selected = folderMode === opt.mode;
                            return (
                              <button
                                key={opt.mode}
                                type="button"
                                onClick={() => {
                                  if (opt.mode === 'search') {
                                    applyFolderKind('search');
                                  } else {
                                    applyFolderKind(buildFolderKind('mailbox', mailboxFolder, isCombined));
                                  }
                                }}
                                className={cn(
                                  'flex items-start gap-3 rounded-xl border p-4 text-left transition-all',
                                  selected
                                    ? cn('ring-2', accent.ring, 'bg-black/[0.02] dark:bg-white/[0.03]')
                                    : cn(colors.border, 'hover:border-gray-300 dark:hover:border-gray-600'),
                                )}
                              >
                                <span
                                  className={cn(
                                    'flex items-center justify-center w-10 h-10 rounded-lg shrink-0',
                                    selected ? accent.iconBg : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                                  )}
                                >
                                  {opt.icon}
                                </span>
                                <span className="min-w-0">
                                  <span className={cn('block text-sm font-semibold', colors.text)}>{opt.label}</span>
                                  <span className={cn('block text-xs mt-0.5', colors.textMuted)}>{opt.hint}</span>
                                </span>
                                {selected && <Check className="w-4 h-4 ml-auto shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />}
                              </button>
                            );
                          })}
                        </div>

                        {folderMode === 'mailbox' && (
                          <div className="mt-4 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {(
                                [
                                  { id: 'inbox' as MailboxFolder, label: 'Inbox', icon: Inbox },
                                  { id: 'sent' as MailboxFolder, label: 'Sent', icon: Send },
                                  { id: 'mySent' as MailboxFolder, label: 'My sent', icon: User },
                                ] as const
                              ).map(({ id, label, icon: Icon }) => {
                                const selected = mailboxFolder === id;
                                return (
                                  <button
                                    key={id}
                                    type="button"
                                    onClick={() => applyFolderKind(buildFolderKind('mailbox', id, isCombined))}
                                    className={cn(
                                      'inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors',
                                      selected
                                        ? cn(accent.iconBg, 'border-transparent text-white')
                                        : cn(colors.border, colors.text, 'hover:bg-black/5 dark:hover:bg-white/5'),
                                    )}
                                  >
                                    <Icon className="w-4 h-4" aria-hidden />
                                    {label}
                                  </button>
                                );
                              })}
                            </div>

                            <AccountScopePicker
                              value={accountScopeMode}
                              onChange={(mode) => setMailboxCombined(mode === 'combined')}
                              colors={colors}
                            />

                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <Label>
                                  {accountScopeMode === 'single' ? 'Which account?' : 'Which accounts to merge?'}
                                </Label>
                                {accountScopeMode === 'combined' && selectedAccountIds.length > 0 && (
                                  <span
                                    className={cn(
                                      'text-xs font-semibold tabular-nums rounded-full px-2 py-0.5',
                                      'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
                                    )}
                                  >
                                    {selectedAccountIds.length} selected
                                  </span>
                                )}
                              </div>
                              <div className={cn('max-h-44 overflow-y-auto dashboard-scrollbar')}>
                                <AccountSwitchList
                                  accounts={accounts ?? []}
                                  selectedAccountIds={selectedAccountIds}
                                  onToggle={toggleAccount}
                                  colors={colors}
                                />
                              </div>
                              {accountScopeMode === 'single' ? (
                                <p className={cn('text-xs', colors.textMuted)}>
                                  Leave unselected to use all accounts you can access.
                                </p>
                              ) : (
                                <>
                                  {selectedAccountIds.length > 0 && selectedAccountIds.length < 2 && (
                                    <p className="text-xs text-amber-700 dark:text-amber-300">
                                      Combined view works best with two or more accounts.
                                    </p>
                                  )}
                                  {selectedAccountIds.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setSelectedAccountIds([])}
                                      className={cn('text-xs font-medium', colors.primaryFg)}
                                    >
                                      Clear selection
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {folderMode === 'search' && (
                          <div className="mt-4 max-w-xs">
                            <Label htmlFor="sf-mailbox-folder">IMAP folder (optional)</Label>
                            <Select
                              id="sf-mailbox-folder"
                              value={folderName}
                              onChange={(e) => setFolderName(e.target.value)}
                              className="w-full mt-1"
                              options={FOLDER_OPTIONS}
                            />
                          </div>
                        )}
                      </FieldGroup>

                    {folderMode === 'search' && (
                      <FieldGroup
                        title="Accounts"
                        description="Leave none selected to include all accounts you can access."
                        colors={colors}
                      >
                        <div className={cn('max-h-48 overflow-y-auto dashboard-scrollbar')}>
                          <AccountSwitchList
                            accounts={accounts ?? []}
                            selectedAccountIds={selectedAccountIds}
                            onToggle={toggleAccount}
                            colors={colors}
                          />
                        </div>
                        {selectedAccountIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedAccountIds([])}
                            className={cn('text-xs font-medium', colors.primaryFg)}
                          >
                            Clear — use all accounts
                          </button>
                        )}
                      </FieldGroup>
                    )}
                  </div>
                )}

                {currentWizardStep === 'filters' && (
                  <FieldGroup
                    title="Filter rules"
                    description="Define which messages appear in this folder."
                    colors={colors}
                  >
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">Match mode</Label>
                        <SegmentedControl
                          value={rulesMatch}
                          options={MATCH_OPTIONS}
                          onChange={setRulesMatch}
                          colors={colors}
                          ariaLabel="Rule match mode"
                        />
                      </div>

                      {rulesMatch !== 'allMessages' && (
                        <div className="space-y-2">
                          {rules.map((rule, index) => (
                            <div
                              key={rule.id}
                              className={cn(
                                'grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_1.5fr_auto] gap-2 items-center rounded-lg border p-3',
                                colors.border,
                              )}
                            >
                              <span className={cn('text-xs font-semibold w-6 text-center', colors.textMuted)}>
                                {index + 1}
                              </span>
                              <Select
                                value={rule.field}
                                onChange={(e) => updateRule(rule.id, { field: e.target.value as SearchRuleField })}
                                options={RULE_FIELD_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                              />
                              <Select
                                value={rule.operator}
                                onChange={(e) =>
                                  updateRule(rule.id, { operator: e.target.value as SearchRuleOperator })
                                }
                                options={RULE_OPERATOR_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                              />
                              <TextInput
                                value={rule.value}
                                onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                                placeholder="e.g. @company.com"
                              />
                              <button
                                type="button"
                                onClick={() => removeRule(rule.id)}
                                disabled={rules.length <= 1}
                                title="Remove rule"
                                className={cn(
                                  'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                                  'text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30',
                                  'disabled:opacity-30 disabled:pointer-events-none',
                                )}
                              >
                                <Trash2 className="w-4 h-4" aria-hidden />
                              </button>
                            </div>
                          ))}
                          <Button type="button" variant="outline" size="sm" onClick={addRule} className="gap-1.5">
                            <Plus className="w-4 h-4" aria-hidden />
                            Add condition
                          </Button>
                        </div>
                      )}
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'period' && (
                  <FieldGroup
                    title="Message period"
                    description="Limit how far back messages are included."
                    colors={colors}
                  >
                    <div className="space-y-3">
                      <SegmentedControl
                        value={periodMode}
                        options={PERIOD_OPTIONS}
                        onChange={setPeriodMode}
                        colors={colors}
                        ariaLabel="Message period"
                      />
                      {periodMode === 'preset' && (
                        <Select
                          value={periodPreset}
                          onChange={(e) => setPeriodPreset(e.target.value as ReceivedDatePreset)}
                          className="w-full max-w-xs"
                          options={ROLLING_RECEIVED_DATE_PRESETS.map((o) => ({
                            value: o.id,
                            label: o.label,
                          }))}
                        />
                      )}
                      {periodMode === 'year' && (
                        <Select
                          value={periodYear.toString()}
                          onChange={(e) => setPeriodYear(parseInt(e.target.value, 10))}
                          className="w-full max-w-xs"
                          options={Array.from({ length: 16 }, (_, i) => {
                            const y = new Date().getUTCFullYear() - i;
                            return { value: y.toString(), label: String(y) };
                          })}
                        />
                      )}
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'sharing' && isEdit && (
                  <FieldGroup title="Sharing" description="Who can see this folder in their Emails sidebar." colors={colors}>
                    {folder ? (
                      <SearchFolderSharesSection folder={folder} />
                    ) : (
                      <div className="py-8 flex justify-center">
                        <LoadingSpinner size="medium" message="Loading folder…" />
                      </div>
                    )}
                  </FieldGroup>
                )}
              </div>

              <div
                className={cn(
                  'flex items-center justify-between gap-3 border-t px-4 py-4 sm:px-6',
                  colors.border,
                  colors.surfaceHover,
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={goWizardBack}
                  disabled={wizardStepIndex === 0}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" aria-hidden />
                  Back
                </Button>
                {isLastWizardStep ? (
                  <Button
                    key="wizard-save"
                    type="button"
                    disabled={saving || !name.trim()}
                    loading={saving}
                    size="sm"
                    onClick={() => void saveFolder()}
                  >
                    {isEdit ? 'Save' : 'Create'}
                  </Button>
                ) : (
                  <Button
                    key="wizard-next"
                    type="button"
                    size="sm"
                    onClick={goWizardNext}
                    disabled={!canProceedWizard}
                    className="gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" aria-hidden />
                  </Button>
                )}
              </div>
            </div>

            {/* Preview */}
            <aside className="lg:sticky lg:top-[4.5rem] space-y-4">
              <div className={cn('rounded-xl border overflow-hidden shadow-sm', colors.border, colors.surface)}>
                <div className={cn('px-4 py-2.5 border-b text-[10px] font-semibold uppercase tracking-wider', colors.border, colors.textMuted)}>
                  Sidebar preview
                </div>
                <div className="p-4 space-y-3">
                  <div className={cn('rounded-lg p-2.5', 'bg-gray-100/80 dark:bg-gray-900/60')}>
                    <p className={cn('text-[10px] font-medium uppercase tracking-wide mb-2 px-1', colors.textMuted)}>
                      Smart Folders
                    </p>
                    <div
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium',
                        'bg-indigo-600/10 text-indigo-900 dark:bg-indigo-500/15 dark:text-indigo-100 ring-1 ring-indigo-500/20',
                      )}
                    >
                      <span className={cn('flex items-center justify-center w-6 h-6 rounded-md shrink-0', accent.iconBg)}>
                        {folderKindIcon(folderKind)}
                      </span>
                      <span className="truncate">{name.trim() || 'Untitled folder'}</span>
                    </div>
                  </div>

                  <p className={cn('text-sm leading-snug', colors.textSecondary)}>{summary.headline}</p>

                  {summary.matchLabel && (
                    <span className={cn('inline-block text-xs font-medium px-2 py-0.5 rounded-full', accent.badge)}>
                      {summary.matchLabel}
                    </span>
                  )}

                  {summary.periodLine && (
                    <p className={cn('text-xs flex items-center gap-1.5', colors.textMuted)}>
                      <Calendar className="w-3.5 h-3.5 shrink-0" aria-hidden />
                      {summary.periodLine}
                    </p>
                  )}

                  {summary.conditionLines.length > 0 && (
                    <ul className={cn('space-y-1 text-xs', colors.textSecondary)}>
                      {summary.conditionLines.map((line, i) => (
                        <li key={i} className="flex gap-1.5">
                          <span className="text-indigo-500">·</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <dl className={cn('pt-3 border-t space-y-1.5 text-xs', colors.border)}>
                    <div className="flex justify-between gap-2">
                      <dt className={colors.textMuted}>Accounts</dt>
                      <dd className={cn('font-medium text-right truncate max-w-[9rem]', colors.text)} title={selectedAccountLabels}>
                        {selectedAccountIds.length === 0 ? 'All' : `${selectedAccountIds.length} selected`}
                      </dd>
                    </div>
                    {folderKind === 'search' && folderName && (
                      <div className="flex justify-between gap-2">
                        <dt className={colors.textMuted}>IMAP folder</dt>
                        <dd className={cn('font-medium', colors.text)}>
                          {FOLDER_OPTIONS.find((o) => o.value === folderName)?.label ?? folderName}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Form;
